import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { getGetFrontRichiesteIdModuloIdDocumentoConsolidatoQueryKey } from '@framework/api-client/api/inquiries-compile/richiesta-moduli/richiesta-moduli.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { useAuthDownload } from '@framework/common-web/utils/download/useAuthDownload.ts';
import { Button, message, Tooltip } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getDocumentoConsolidatoOmissisQueryOptions } from '../../download-pdf/getDocumentoConsolidatoOmissisQueryOptions.ts';
import { getDocumentoConsolidatoWithoutParamsQueryOptions } from '../../download-pdf/getDocumentoConsolidatoWithoutParamsQueryOptions.ts';

interface IDownloadModuleButtonProps {
  submissionId: string;
  submissionModuleId: string;
  disabled?: boolean;
  isOmissis?: boolean;
  /** Se true, apre il file nel viewer inline */
  viewInline?: boolean;
  /** Suffisso per differenziare la query key quando più istanze condividono lo stesso endpoint */
  queryKeySuffix?: string;
}

/**
 * Pulsante solo-icona per download o visualizzazione inline del PDF di un modulo.
 * Speculare a DownloadAttachmentButton per gli allegati.
 */
export function DownloadModuleButton(props: IDownloadModuleButtonProps) {
  const {
    submissionId,
    submissionModuleId,
    disabled,
    isOmissis,
    viewInline,
    queryKeySuffix,
  } = props;
  const { t } = useTranslation();

  const getQueryOptions = isOmissis
    ? getDocumentoConsolidatoOmissisQueryOptions
    : getDocumentoConsolidatoWithoutParamsQueryOptions;

  const { download, isFetching } = useAuthDownload(
    getQueryOptions,
    [submissionId, submissionModuleId],
    {
      onError: e => {
        console.error('[DownloadModuleButton] Error: ', { error: e });
        message.error(
          getNetworkErrorMessage(
            e,
            t(
              'submission-module-button.errore-in-download',
              'Si è verificato un problema durante il download del file',
            ),
          ),
        );
      },
      showInlineViewer: viewInline ?? false,
      ...(queryKeySuffix && {
        queryOptions: {
          queryKey: [
            ...getGetFrontRichiesteIdModuloIdDocumentoConsolidatoQueryKey(
              submissionId,
              submissionModuleId,
              isOmissis ? { omissis: true } : {},
            ),
            queryKeySuffix,
          ],
        },
      }),
    },
  );

  const handleClick = useCallback(() => {
    if (submissionId && submissionModuleId && !disabled) {
      download();
    }
  }, [download, disabled, submissionId, submissionModuleId]);

  const icon = viewInline ? (
    <EyeOutlined
      aria-label={t('submission-summary.visualizza', 'Visualizza')}
    />
  ) : (
    <DownloadOutlined aria-label={t('submission-summary.scarica', 'Scarica')} />
  );

  const text = viewInline
    ? t('submission-summary.visualizza', 'Visualizza')
    : t('submission-summary.scarica', 'Scarica');

  return (
    <Tooltip
      title={
        disabled
          ? t(
              'form.salva-modulo-download-pdf',
              'Salva il modulo almeno una volta per scaricare il documento PDF',
            )
          : text
      }
    >
      <Button
        icon={icon}
        onClick={handleClick}
        disabled={!submissionId || !submissionModuleId || disabled}
        loading={isFetching}
        size="small"
        type="link"
      />
    </Tooltip>
  );
}
